import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  CssBaseline,
  TextField,
  Paper,
  Box,
  Grid,
  Typography,
  createTheme,
  ThemeProvider,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Logo from "../../logo.svg";
import commonApi from "../../utils/api";
import { CommonContext } from "../../context";
import AlertMessage from "./AlertMessage";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CryptoJS from "crypto-js";
import Loader from "./Loader ";

const useQueryParams = () => {
  const { search } = useLocation();
  return Object.fromEntries(new URLSearchParams(search));
};

const secretKey = "app-secret";

const encryptData = (data, secretKey) => {
  return CryptoJS.AES.encrypt(JSON.stringify(data), secretKey).toString();
};

const decryptData = (encryptedData, secretKey) => {
  const bytes = CryptoJS.AES.decrypt(encryptedData, secretKey);
  return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
};

const defaultTheme = createTheme();

export default function MerchantSignUp() {
  const {
    isLoading,
    setIsLoading,
    openAlert,
    setSeverity,
    setAlertMesaage,
    setOpenAlert,
    setUser,
  } = useContext(CommonContext);

  const [formErrors, setFormErrors] = useState({});
  const [formValues, setFormValues] = useState({
    brand_name: "",
    company_name: "",
    email: "",
    mobile: "",
    password: "",
    confirm_password: "",
    monthly_orders: "",
    termsA_accepted: false,
  });

  const navigate = useNavigate();

  useEffect(() => {
    const encryptedUser = localStorage.getItem("userApp");
    if (encryptedUser) {
      const user = decryptData(encryptedUser, secretKey);
      setUser(user);
    }
  }, [setUser]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    const errors = {};
    if (!formValues.brand_name) errors.brand_name = "Brand Name is required";
    if (!formValues.company_name) errors.company_name = "Company Name is required";
    if (!formValues.email) {
      errors.email = "Email is required";
    } else if (!/^\S+@\S+\.\S+$/.test(formValues.email)) {
      errors.email = "Invalid email format";
    }
    if (!formValues.mobile) {
      errors.mobile = "Mobile number is required";
    } else if (!/^\d{10}$/.test(formValues.mobile)) {
      errors.mobile = "Enter a valid 10-digit mobile number";
    }
    if (!formValues.password) {
      errors.password = "Password is required";
    }
    if (!formValues.confirm_password) {
      errors.confirm_password = "Confirm Password is required";
    } else if (formValues.password !== formValues.confirm_password) {
      errors.confirm_password = "Passwords do not match";
    }
    if (!formValues.monthly_orders) {
      errors.monthly_orders = "Estimated Monthly Orders is required";
    }
    if (!formValues.termsA_accepted) {
      errors.termsA_accepted = "You must accept the Privacy Policy & Terms";
    }
    return errors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    const errors = validate();
    setFormErrors(errors);

    if (Object.keys(errors).length === 0) {
      setIsLoading(true);

      const data = {
        brand_name: formValues.brand_name,
        company_name: formValues.company_name,
        email: formValues.email,
        mobile: formValues.mobile,
        password: formValues.password,
        monthly_orders: formValues.monthly_orders,
      };

      commonApi
        .post("/signup/add", data)
        .then((response) => {
          setIsLoading(false);
          if (response.data.s === 1) {
            // const userData = response?.data?.data;
            // const encryptedUserData = encryptData(userData, secretKey);
            // localStorage.setItem("userApp", encryptedUserData);
            // localStorage.setItem("userToke", response?.data?.token);
            // localStorage.setItem("userType", "Merchant");
            // localStorage.setItem(
            //   "ordersEvent",
            //   JSON.stringify(response?.data?.events)
            // );
            // setUser(userData);
            // setTimeout(() => {
            //   navigate("/");
            // }, 1000);
            setAlertMesaage(response?.data?.msg);
            setSeverity("success");
            setOpenAlert(true);
            setFormValues({
              brand_name: "",
              company_name: "",
              email: "",
              mobile: "",
              password: "",
              confirm_password: "",
              monthly_orders: "",
              termsA_accepted: false,
            });
          } else {
            setAlertMesaage(response?.data?.msg);
            setSeverity("error");
            setOpenAlert(true);
          }
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          setIsLoading(false);
          setAlertMesaage(error?.message || error?.response?.data?.msg || "Something went wrong");
          setSeverity("error");
          setOpenAlert(true);
        });
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      {isLoading && <Loader />}
      {openAlert && <AlertMessage />}
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: "url(./img/bg1.jpg)",
            backgroundRepeat: "no-repeat",
            backgroundColor: (t) =>
              t.palette.mode === "light"
                ? t.palette.grey[50]
                : t.palette.grey[900],
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <Grid
          item
          xs={12}
          sm={8}
          md={5}
          component={Paper}
          elevation={6}
          square
        >
          <Box
            sx={{
              my: 3,
              mx: 4,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Box sx={{ my: 2 }}>
              <img src={Logo} alt="Logo" loading="lazy" />
            </Box>
            <Typography component="h1" variant="h5">
              Sign Up
            </Typography>
            <Box
              component="form"
              noValidate
              onSubmit={handleSubmit}
              sx={{ mt: 1 }}
            >
              <TextField
                margin="normal"
                fullWidth
                required
                id="brand_name"
                label="Brand Name"
                name="brand_name"
                value={formValues.brand_name}
                onChange={handleChange}
                error={!!formErrors.brand_name}
                helperText={formErrors.brand_name}
              />

              <TextField
                margin="normal"
                fullWidth
                required
                id="company_name"
                label="Company Name"
                name="company_name"
                value={formValues.company_name}
                onChange={handleChange}
                error={!!formErrors.company_name}
                helperText={formErrors.company_name}
              />

              <TextField
                margin="normal"
                fullWidth
                required
                id="email"
                label="Email Address"
                name="email"
                value={formValues.email}
                onChange={handleChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />

              <TextField
                margin="normal"
                fullWidth
                required
                id="mobile"
                label="Mobile Number"
                name="mobile"
                value={formValues.mobile}
                onChange={handleChange}
                error={!!formErrors.mobile}
                helperText={formErrors.mobile}
              />

              <TextField
                margin="normal"
                fullWidth
                required
                name="password"
                label="Password"
                type="password"
                id="password"
                value={formValues.password}
                onChange={handleChange}
                error={!!formErrors.password}
                helperText={formErrors.password}
              />

              <TextField
                margin="normal"
                fullWidth
                required
                name="confirm_password"
                label="Confirm Password"
                type="password"
                id="confirm_password"
                value={formValues.confirm_password}
                onChange={handleChange}
                error={!!formErrors.confirm_password}
                helperText={formErrors.confirm_password}
              />
              <TextField
                margin="normal"
                fullWidth
                required
                name="monthly_orders"
                label="Estimated Monthly Orders"
                type="number"
                id="monthly_orders"
                value={formValues.monthly_orders}
                onChange={handleChange}
                error={!!formErrors.monthly_orders}
                helperText={formErrors.monthly_orders}
              />

              <FormControlLabel
                sx={{ width: "100%" }}
                control={
                  <Checkbox
                    name="termsA_accepted"
                    checked={formValues.termsA_accepted}
                    onChange={handleChange}
                    color="primary"
                  />
                }
                label={
                  <span>
                    I agree to the{" "}
                    <Link
                      to="/privacy-policy"
                      style={{
                        textDecoration: "none",
                        color: "#1976d2",
                        fontWeight: 500,
                      }}
                    >
                      Privacy Policy & Terms
                    </Link>
                  </span>
                }
              />

              {formErrors.termsA_accepted && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ display: "block", mt: -1 }}
                >
                  {formErrors.termsA_accepted}
                </Typography>
              )}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
              >
                Sign Up
              </Button>
              <Typography variant="body2" align="center">
                Already have an account?{" "}
                <Link
                  to="/login"
                  style={{ textDecoration: "none", color: "#1976d2", fontWeight: 500 }}
                >
                  Sign in instead
                </Link>
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}
